import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { trackRecipeExported, trackRecipeFavorite } from "@/lib/analytics-service";
import {
    exportRecipeToCSV,
    exportRecipeToJSON,
    exportRecipeToMarkdown,
    exportRecipeToPDF,
    exportRecipeToPlainText,
    exportRecipeToRecipeML,
} from "@/lib/recipe-export-service";
import { toggleRecipeFavorite } from "@/lib/recipe-service";
import type { AccountTier, Recipe, RecipeBox } from "@/lib/types";
import { format } from "date-fns";
import { Copy, Download, Edit, FileCode, FileJson, FileSpreadsheet, FileText, FileType, Loader2, Printer, Share2, Star, Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { useUserDisplayName } from "@/hooks/useUserDisplayName";

export type RecipeHeaderProps = Readonly<{
  recipe: Recipe;
  recipeBox: RecipeBox | null;
  createdDate: Date | null;
  updatedDate: Date | null;
  isOwner: boolean;
  canDelete: boolean;
  canExport: boolean;
  canClone: boolean;
  accountTier?: AccountTier;
  isEditing: boolean;
  onEnterEdit: () => void;
  onExitEdit: () => void;
  onShare: () => void | Promise<void>;
  onConfirmDelete: () => void | Promise<void>;
  onCloneSameBox: () => void | Promise<void>;
  onCloneDifferentBox: () => void;
  onPrint: () => void;
  isDeleting: boolean;
  userId?: string | null;
  onFavoriteToggle?: (isFavorite: boolean) => void;
  onDraftToggle?: (isDraft: boolean) => void;
}>;

export const RecipeHeader = ({
  recipe,
  recipeBox,
  createdDate,
  updatedDate,
  isOwner,
  canDelete,
  canExport,
  canClone,
  accountTier,
  isEditing,
  onEnterEdit,
  onExitEdit,
  onShare,
  onConfirmDelete,
  onCloneSameBox,
  onCloneDifferentBox,
  onPrint,
  isDeleting,
  userId,
  onFavoriteToggle,
  onDraftToggle,
}: RecipeHeaderProps) => {
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  const [optimisticFavorite, setOptimisticFavorite] = useState<boolean | null>(null);
  const [isTogglingDraft, setIsTogglingDraft] = useState(false);
  const [optimisticDraft, setOptimisticDraft] = useState<boolean | null>(null);
  
  // Fetch display names for creator and updater
  const createdByName = useUserDisplayName(recipe.createdByUid);
  const updatedByName = useUserDisplayName(recipe.updatedByUid);
  
  const createdLabel = createdDate ? 
    (createdByName ? `Created by ${createdByName} on ${format(createdDate, "PPP")}` : `Created ${format(createdDate, "PPP")}`) : null;
  const updatedLabel = updatedDate ? 
    (updatedByName ? `Last updated by ${updatedByName} on ${format(updatedDate, "PPP")}` : `Last updated ${format(updatedDate, "PPP")}`) : null;
  const isFreeTier = accountTier === "free" || !accountTier;

  // Use optimistic state if available, otherwise fall back to prop
  // The optimistic state will be cleared after the async operation completes,
  // allowing Firestore subscription updates to take over
  const isFavorite = optimisticFavorite !== null ? optimisticFavorite : (recipe.isFavorite ?? false);
  const isDraft = optimisticDraft !== null ? optimisticDraft : (recipe.isDraft ?? false);

  const handleToggleFavorite = async () => {
    if (!userId || isTogglingFavorite) {
      return;
    }

    // Optimistic update - update UI immediately
    const newFavoriteStatus = !isFavorite;
    setOptimisticFavorite(newFavoriteStatus);
    setIsTogglingFavorite(true);

    try {
      const confirmedFavoriteStatus = await toggleRecipeFavorite(recipe.id, userId, isFavorite, recipeBox);
      trackRecipeFavorite(confirmedFavoriteStatus);
      // Clear optimistic state - Firestore subscription will update the prop
      setOptimisticFavorite(null);
      onFavoriteToggle?.(confirmedFavoriteStatus);
    } catch (error) {
      console.error("Failed to toggle favorite", error);
      // Revert optimistic update on error - show original state
      setOptimisticFavorite(null);
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  const handleToggleDraft = async () => {
    if (!userId || !isOwner || isTogglingDraft) {
      return;
    }

    // Optimistic update - update UI immediately
    const newDraftStatus = !isDraft;
    setOptimisticDraft(newDraftStatus);
    setIsTogglingDraft(true);

    try {
      const { updateRecipe } = await import("@/lib/recipe-service");
      await updateRecipe(userId, recipe.id, { isDraft: newDraftStatus }, recipeBox);
      // Clear optimistic state - Firestore subscription will update the prop
      setOptimisticDraft(null);
      onDraftToggle?.(newDraftStatus);
    } catch (error) {
      console.error("Failed to toggle draft", error);
      // Revert optimistic update on error - show original state
      setOptimisticDraft(null);
    } finally {
      setIsTogglingDraft(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      await exportRecipeToPDF(recipe, recipeBox, isFreeTier);
      trackRecipeExported("pdf");
    } catch (error) {
      console.error("Failed to export PDF", error);
    }
  };

  const handleExportJSON = () => {
    exportRecipeToJSON(recipe, recipeBox);
    trackRecipeExported("json");
  };

  const handleExportCSV = () => {
    exportRecipeToCSV(recipe, recipeBox);
    trackRecipeExported("csv");
  };

  const handleExportRecipeML = () => {
    exportRecipeToRecipeML(recipe, recipeBox);
    trackRecipeExported("recipeml");
  };

  const handleExportMarkdown = () => {
    exportRecipeToMarkdown(recipe, recipeBox);
    trackRecipeExported("markdown");
  };

  const handleExportPlainText = () => {
    exportRecipeToPlainText(recipe, recipeBox);
    trackRecipeExported("plaintext");
  };

  let ownerAction: ReactNode = null;
  if (isOwner) {
    ownerAction = isEditing ? (
      <Button variant="outline" size="sm" onClick={onExitEdit}>
        Cancel
      </Button>
    ) : (
      <Button variant="outline" size="sm" onClick={onEnterEdit}>
        <Edit className="mr-2 h-4 w-4" />
        Edit
      </Button>
    );
  }

  return (
    <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
      <div>
        <h1 className="font-headline text-3xl font-bold md:text-4xl">{recipe.title}</h1>
        <div className="mt-3 flex flex-wrap gap-2">
          {recipe.labels.length > 0 ? (
            recipe.labels.map((label) => (
              <Badge key={label} variant="secondary">
                {label}
              </Badge>
            ))
          ) : (
            <span className="text-sm text-muted-foreground">No labels added yet.</span>
          )}
        </div>
        {(createdLabel || updatedLabel) && (
          <div className="mt-2 text-sm text-muted-foreground">
            {createdLabel && <p>{createdLabel}</p>}
            {updatedLabel && <p>{updatedLabel}</p>}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {ownerAction}

        {isOwner && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleToggleDraft}
            disabled={isTogglingDraft}
            className={isDraft ? "bg-gray-50 border-gray-300 hover:bg-gray-100" : ""}
          >
            {isTogglingDraft ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isDraft ? "Publishing..." : "Saving..."}
              </>
            ) : (
              <>
                {isDraft ? "📝 Draft" : "✓ Published"}
              </>
            )}
          </Button>
        )}

        {userId && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleToggleFavorite}
            disabled={isTogglingFavorite}
            className={isFavorite ? "bg-yellow-50 border-yellow-300 hover:bg-yellow-100" : ""}
          >
            {isTogglingFavorite ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isFavorite ? "Removing..." : "Adding..."}
              </>
            ) : (
              <>
                <Star className={`mr-2 h-4 w-4 ${isFavorite ? "fill-yellow-500 text-yellow-500" : ""}`} />
                {isFavorite ? "Favorited" : "Favorite"}
              </>
            )}
          </Button>
        )}

        <Button variant="outline" size="sm" onClick={onShare}>
          <Share2 className="mr-2 h-4 w-4" />
          Share
        </Button>

        {canClone && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Copy className="mr-2 h-4 w-4" />
                Clone
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onCloneSameBox}>
                <Copy className="mr-2 h-4 w-4" />
                Clone in Same Box
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onCloneDifferentBox}>
                <Copy className="mr-2 h-4 w-4" />
                Clone to Different Box
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {canExport && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportPDF}>
                <FileText className="mr-2 h-4 w-4" />
                Export as PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportJSON}>
                <FileJson className="mr-2 h-4 w-4" />
                Export as JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportCSV}>
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleExportMarkdown}>
                <FileType className="mr-2 h-4 w-4" />
                Export as Markdown
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPlainText}>
                <FileText className="mr-2 h-4 w-4" />
                Export as Plain Text
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportRecipeML}>
                <FileCode className="mr-2 h-4 w-4" />
                Export as RecipeML
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <Button variant="outline" size="sm" onClick={onPrint}>
          <Printer className="mr-2 h-4 w-4" />
          Print
        </Button>

        {canDelete ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this recipe?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. The recipe will be removed from the recipe box.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onConfirmDelete} disabled={isDeleting}>
                  {isDeleting ? "Deleting..." : "Delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : null}
      </div>
    </header>
  );
};

